/**
 * Notification Polling Service
 * 
 * Serviço de fallback para garantir notificações quando Socket.IO falha.
 * Inspirado no comportamento do WhatsApp Web.
 * 
 * Estratégia:
 * - Socket conectado → Polling desativado (economia de recursos)
 * - Socket desconectado → Polling ativo a cada 15 segundos
 * - Detecta novos tickets → Dispara Web Notification + som
 * 
 * @author OmniPlay Team
 * @version 1.0.0
 */

import api from "./api";
import pushNotificationService from "./pushNotificationService";

// Configurações
const POLLING_INTERVAL = 15000; // 15 segundos quando socket desconectado
const POLLING_INTERVAL_BACKGROUND = 30000; // 30 segundos quando aba em background
const MAX_RETRIES = 3;

// ✅ Log condicional para não poluir console em produção
const isDev = process.env.NODE_ENV === "development";
const log = (...args) => isDev && console.log(...args);
const logWarn = (...args) => console.warn(...args);
const logError = (...args) => console.error(...args);

class NotificationPollingService {
  constructor() {
    this.isPollingActive = false;
    this.pollingInterval = null;
    this.lastKnownTicketIds = new Set();
    this.lastPollTime = null;
    this.retryCount = 0;
    this.isTabVisible = !document.hidden;
    this.onNewTicketCallbacks = [];
    this.socketConnected = false;
    this.userQueueIds = [];
    this.companyId = null;
    
    // Listener para visibilidade da aba
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    document.addEventListener("visibilitychange", this.handleVisibilityChange);
  }

  /**
   * Inicializa o serviço com dados do usuário
   */
  initialize(companyId, userQueueIds) {
    this.companyId = companyId;
    this.userQueueIds = userQueueIds;
    log("[PollingService] Inicializado para company:", companyId);
  }

  /**
   * Callback quando visibilidade da aba muda
   */
  handleVisibilityChange() {
    const wasVisible = this.isTabVisible;
    this.isTabVisible = !document.hidden;
    
    if (this.isTabVisible && !wasVisible && this.isPollingActive) {
      // Aba voltou a ser visível - fazer poll imediato
      log("[PollingService] Aba visível - poll imediato");
      this.poll();
      
      // Ajustar intervalo de volta ao normal
      this.adjustPollingInterval();
    } else if (!this.isTabVisible && wasVisible && this.isPollingActive) {
      // Aba ficou invisível - aumentar intervalo
      this.adjustPollingInterval();
    }
  }

  /**
   * Ajusta intervalo de polling baseado na visibilidade da aba
   */
  adjustPollingInterval() {
    if (!this.isPollingActive) return;
    
    // Parar intervalo atual
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
    }
    
    // Definir novo intervalo
    const interval = this.isTabVisible ? POLLING_INTERVAL : POLLING_INTERVAL_BACKGROUND;
    this.pollingInterval = setInterval(() => this.poll(), interval);
    
    log(`[PollingService] Intervalo ajustado para ${interval / 1000}s`);
  }

  /**
   * Notifica que o socket conectou/desconectou
   */
  setSocketStatus(connected) {
    const wasConnected = this.socketConnected;
    this.socketConnected = connected;
    
    if (connected && !wasConnected) {
      // Socket reconectou - parar polling
      log("[PollingService] Socket conectado - parando polling");
      this.stopPolling();
    } else if (!connected && wasConnected) {
      // Socket desconectou - iniciar polling
      log("[PollingService] Socket desconectado - iniciando polling fallback");
      this.startPolling();
    }
  }

  /**
   * Inicia o polling
   */
  startPolling() {
    if (this.isPollingActive) return;
    
    this.isPollingActive = true;
    this.retryCount = 0;
    
    // Poll imediato
    this.poll();
    
    // Agendar próximos polls
    const interval = this.isTabVisible ? POLLING_INTERVAL : POLLING_INTERVAL_BACKGROUND;
    this.pollingInterval = setInterval(() => this.poll(), interval);
    
    log(`[PollingService] Polling iniciado - intervalo ${interval / 1000}s`);
  }

  /**
   * Para o polling
   */
  stopPolling() {
    if (!this.isPollingActive) return;
    
    this.isPollingActive = false;
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
    
    log("[PollingService] Polling parado");
  }

  /**
   * Executa um poll para buscar novos tickets
   */
  async poll() {
    if (!this.companyId) {
      logWarn("[PollingService] Não inicializado - ignorando poll");
      return;
    }
    
    try {
      const { data } = await api.get("/tickets", {
        params: {
          status: "pending",
          pageNumber: 1,
          queueIds: JSON.stringify(this.userQueueIds),
          showAll: "false",
        },
      });
      
      if (!data || !Array.isArray(data.tickets)) {
        return;
      }
      
      this.retryCount = 0; // Reset retry count on success
      this.lastPollTime = Date.now();
      
      // Detectar novos tickets
      const currentTicketIds = new Set(data.tickets.map(t => t.id));
      const newTickets = data.tickets.filter(t => !this.lastKnownTicketIds.has(t.id));
      
      // Notificar sobre novos tickets
      if (newTickets.length > 0 && this.lastKnownTicketIds.size > 0) {
        log(`[PollingService] ${newTickets.length} novo(s) ticket(s) detectado(s)`);
        this.notifyNewTickets(newTickets);
      }
      
      // Atualizar lista de IDs conhecidos
      this.lastKnownTicketIds = currentTicketIds;
      
    } catch (error) {
      this.retryCount++;
      logWarn(`[PollingService] Erro no poll (tentativa ${this.retryCount}/${MAX_RETRIES}):`, error.message);
      
      if (this.retryCount >= MAX_RETRIES) {
        logError("[PollingService] Máximo de tentativas atingido - parando polling");
        this.stopPolling();
      }
    }
  }

  /**
   * Notifica callbacks sobre novos tickets
   */
  notifyNewTickets(tickets) {
    this.onNewTicketCallbacks.forEach(callback => {
      try {
        callback(tickets);
      } catch (error) {
        logError("[PollingService] Erro no callback:", error);
      }
    });
    
    // Disparar Web Notification se permitido
    this.showWebNotification(tickets);
  }

  /**
   * Mostra Web Notification do sistema operacional
   */
  showWebNotification(tickets) {
    if (pushNotificationService.getPermission() !== "granted") return;
    if (this.isTabVisible) return;
    
    tickets.forEach(ticket => {
      try {
        const contactName = ticket.contact?.name || "Novo cliente";
        const lastMessage = ticket.lastMessage || "Nova mensagem";
        
        pushNotificationService.showNotification(`${contactName}`, {
          body: lastMessage.substring(0, 100),
          tag: `ticket-${ticket.id}`,
          requireInteraction: false,
          silent: false,
          data: {
            url: `/tickets/${ticket.uuid || ticket.id}`,
            ticketId: ticket.id,
            ticketUuid: ticket.uuid,
            ticketStatus: ticket.status,
          },
        });
      } catch (error) {
        logError("[PollingService] Erro ao criar notificação:", error);
      }
    });
  }

  /**
   * Registra callback para novos tickets
   */
  onNewTicket(callback) {
    this.onNewTicketCallbacks.push(callback);
    return () => {
      this.onNewTicketCallbacks = this.onNewTicketCallbacks.filter(cb => cb !== callback);
    };
  }

  /**
   * Força um poll imediato (útil após reconexão)
   */
  forcePoll() {
    if (this.companyId) {
      this.poll();
    }
  }

  /**
   * Limpa recursos do serviço
   */
  destroy() {
    this.stopPolling();
    document.removeEventListener("visibilitychange", this.handleVisibilityChange);
    this.onNewTicketCallbacks = [];
  }
}

// Singleton
const notificationPollingService = new NotificationPollingService();

export default notificationPollingService;
